import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { initAuth, msalInstance } from '@/auth/msal-config';
import { resolveAuth, type AuthOutcome } from '@/auth/auth-gate';
import { AuthError } from '@/components/auth-error';
import { watchSystemTheme } from '@/lib/theme';
import { App } from './App';
import './index.css';

// Keep `system` theme in sync with OS changes (initial theme is applied inline
// in index.html before paint to avoid a flash).
watchSystemTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

async function bootstrap(): Promise<void> {
  let outcome: AuthOutcome;
  try {
    // Resolve /config.json (deployed) or the VITE_* fallback (local dev) before MSAL
    // comes up — EW-242, the admin FE's EW-166 pattern. The try/catch is the net for
    // an unexpected throw (e.g. `new URL()` on an https-prefixed but malformed
    // authority): `void bootstrap()` would otherwise end in an unhandled rejection
    // and a blank page (the admin FE wraps its bootstrap the same way).
    await initAuth();
    outcome = await resolveAuth();
  } catch (error) {
    // The failed branch below logs the detail to the console.
    outcome = { kind: 'failed', detail: error instanceof Error ? error.message : String(error) };
  }
  if (outcome.kind === 'redirecting') return;

  const root = createRoot(document.getElementById('root')!);

  if (outcome.kind !== 'ready') {
    if (outcome.kind === 'failed' && outcome.detail) {
      console.error('EchoProxy sign-in failed:', outcome.detail);
    }
    root.render(
      <StrictMode>
        <AuthError outcome={outcome} />
      </StrictMode>,
    );
    return;
  }

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
